import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';

const NewUser: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [inviteCode, setInviteCode] = useState('');
  const [isJoiningLeague, setIsJoiningLeague] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user already has leagues - if so, redirect to dashboard
  useEffect(() => {
    const checkUserLeagues = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        // Just set loading to false - don't redirect users with existing leagues
        setLoading(false);
      } catch (err) {
        console.error('Error checking user leagues:', err);
        setLoading(false);
      }
    };

    checkUserLeagues();
  }, [user, navigate]);

  const handleJoinInviteLeague = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode.trim()) {
      setError('Please enter an invite code');
      return;
    }

    setIsJoiningLeague(true);
    setError(null);

    try {
      const response = await apiClient.post('/leagues/join-by-code', {
        inviteCode: inviteCode.trim()
      });
      
      // Clear the league cache so the joined league appears immediately in the leagues list
      sessionStorage.removeItem('leagues_data');
      sessionStorage.removeItem('user_leagues_data');
      sessionStorage.removeItem('leagues_cache_timestamp');
      
      // Redirect to the joined league
      navigate(`/leagues/${response.data.membership.leagueId}`);
    } catch (err: any) {
      console.error('Error joining league:', err);
      setError(err.response?.data?.message || 'Invalid invite code. Please check and try again.');
    } finally {
      setIsJoiningLeague(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="text-xl" style={{ color: 'var(--text-primary)' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto mt-8 px-4 md:px-6">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          Welcome to PredictorPro!
        </h1>
        <p className="text-lg opacity-70" style={{ color: 'var(--text-primary)' }}>
          You're all set up! Here's how you can get started:
        </p>
        
        {/* Success message from previous page */}
        {location.state?.message && (
          <div className="mt-4 p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg">
            {location.state.message}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">

        {/* Option 3: Make Predictions Without League */}
        <div className="card p-6 text-center hover:shadow-lg transition-shadow">
          <div className="text-4xl mb-4">⚽</div>
          <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Start Predicting
          </h3>
          <p className="text-sm opacity-70 mb-4" style={{ color: 'var(--text-primary)' }}>
            Select a competition and predict match results and scores
          </p>
          <Link 
            to="/seasons"
            className="btn-primary w-full text-sm"
          >
            Browse Matches
          </Link>
        </div>

        {/* Option 1: Join Existing League */}
{/*         <div className="card p-6 text-center hover:shadow-lg transition-shadow">
          <div className="text-4xl mb-4">🏆</div>
          <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Join an Existing League
          </h3>
          <p className="text-sm opacity-70 mb-4" style={{ color: 'var(--text-primary)' }}>
            Join public leagues to compete with other players worldwide
          </p>
          <Link 
            to="/leagues"
            className="btn-primary w-full text-sm"
          >
            Browse Leagues
          </Link>
        </div> */}

        {/* Option 2: Join Friend's League */}
        <div className="card p-6 text-center hover:shadow-lg transition-shadow">
          <div className="text-4xl mb-4">💬</div>
          <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Join Friends League
          </h3>
          <p className="text-sm opacity-70 mb-4" style={{ color: 'var(--text-primary)' }}>
            Use an invite code to join a private league with your friends
          </p>
          <button 
            onClick={() => document.getElementById('invite-modal')?.classList.remove('hidden')}
            className="btn-secondary w-full text-sm"
          >
            Enter Invite Code
          </button>
        </div>

        {/* Option 3: Create Friend's League */}
        <div className="card p-6 text-center hover:shadow-lg transition-shadow">
          <div className="text-4xl mb-4">🏆</div>
          <h3 className="text-xl font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Create Friends League
          </h3>
          <p className="text-sm opacity-70 mb-4" style={{ color: 'var(--text-primary)' }}>
            Start your own private league and invite friends with a code
          </p>
          <Link
            to="/leagues"
            state={{ openCreateForm: true }}
            className="btn-primary w-full text-sm"
          >
            Create League
          </Link>
        </div>


      </div>

      {/* Quick Tips Section */}
      <div className="card p-6 bg-gray-50 dark:bg-gray-800/50">
        <div className="grid md:grid-cols-2 gap-4">
          <div>

          <strong className="">Scoring Explained</strong>
          <p className="text-sm mt-4 mb-4" style={{ color: 'var(--text-secondary)' }}>
          🥇 &nbsp; 1 point for predicting the correct result
          </p>
          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
          ⚽️ &nbsp; 3 points for predicting the exact score
          </p>
          <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
          💷 &nbsp; Banker doubles your points if correct, but lose 1pt if incorrect (available once per round)
          </p>

          </div>
          <div>
          <strong className="">Getting Started</strong>

              <p className="text-sm mt-4 mb-4" style={{ color: 'var(--text-secondary)' }}>
                ✅ &nbsp; Join a league, create your own or just select your favourite competition and start predicting
              </p>
              <p className="text-sm mt-4 mb-4" style={{ color: 'var(--text-secondary)' }}>
                ✅ &nbsp; Make your predictions for the upcoming week
              </p>
              <p className="text-sm mt-4 mb-4" style={{ color: 'var(--text-secondary)' }}>
                ✅ &nbsp; Check your scores after each match and try to top the leaderboard!
              </p>
          </div>
        </div>
      </div>

      {/* Skip for now */}
      <div className="text-center mt-6">
        <Link 
          to="/"
          className="text-sm opacity-70 hover:opacity-100 transition-opacity"
          style={{ color: 'var(--text-primary)' }}
        >
          Skip for now, I'll explore later →
        </Link>
      </div>

      {/* Invite Code Modal */}
      <div 
        id="invite-modal" 
        className="hidden fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            e.currentTarget.classList.add('hidden');
          }
        }}
      >
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
          <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            Join League with Invite Code
          </h3>
          
          <form onSubmit={handleJoinInviteLeague}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Invite Code
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                placeholder="Enter the invite code"
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                style={{ color: 'var(--text-primary)' }}
                disabled={isJoiningLeague}
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={isJoiningLeague}
                className="btn-primary flex-1"
              >
                {isJoiningLeague ? 'Joining...' : 'Join League'}
              </button>
              <button
                type="button"
                onClick={() => document.getElementById('invite-modal')?.classList.add('hidden')}
                className="btn-secondary flex-1"
                disabled={isJoiningLeague}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewUser; 